import './DashboardPage.scss'
import { ResponsiveBox, SpeedDialAction } from 'devextreme-react'
import { Row, Col, ResponsiveBoxRef } from 'devextreme-react/responsive-box'
import { dashboardPageRouterLoader } from '@/routes/home/DashboardPage.route'
import { useLoaderData } from 'react-router-typesafe'
import DocumentHead from '@/components/document-head/DocumentHead'
import { Fragment, useEffect, useRef, useState } from 'react'
import { ClickEvent } from 'devextreme/ui/button'
import { Item as ResponsiveItem, Location } from 'devextreme-react/responsive-box'
import { config } from 'devextreme/common'
import { SpeedDialActionTypes } from 'devextreme-react/cjs/speed-dial-action'
import { Card } from '@/routes/home/card/Card.enums'
import notify from 'devextreme/ui/notify'
import { isUserRoleAllowed } from '@/routes/utils'
import { Roles } from '@/auth/azure/Roles'
import DashboardCard from '@/routes/home/card/DashboardCard'
import { useScreenSize } from '@/themes/media-query'

const title: string = 'Dashboard'
const maxCardsVisible: number = 4

function DashboardPage() {
  const { getCurrentEmployee, dashboardCards, userInfo, dashboardNormalView } =
    useLoaderData<typeof dashboardPageRouterLoader>()
  const [dbCards, setDbCards] = useState<string[]>(dashboardCards)
  const [dbNormalView, setDbNormalView] = useState<boolean>(dashboardNormalView)
  const currentScreenSize = useScreenSize()
  const responsiveBoxRef = useRef<ResponsiveBoxRef>(null)

  const updateCards = (newDbCards: string[]) => {
    setDbCards(newDbCards)
    localStorage.setItem(`dashboard-cards`, JSON.stringify(newDbCards))
  }

  const removeCard = (newDbCards: string[]) => {
    updateCards(newDbCards)
  }

  const switchCards = (currentIndex: number, destinationIndex: number) => {
    const newDbCards = [...dbCards]
    newDbCards[currentIndex] = dbCards[destinationIndex]
    newDbCards[destinationIndex] = dbCards[currentIndex]
    updateCards(newDbCards)
  }

  const switchViewMode = (view: boolean) => {
    const newDbCards = [...dbCards]
    let card: string | undefined
    if (view) {
      card = newDbCards.pop()
      newDbCards.unshift(card ?? '')
    } else {
      card = newDbCards.shift()
      newDbCards.push(card ?? '')
    }
    setDbNormalView(view)
    localStorage.setItem(`dashboard-normal-view`, view ? 'true' : 'false')
    updateCards(newDbCards)
  }

  useEffect(() => {
    const getOffset = () => {
      if (currentScreenSize.isXSmall) return '-12 -12' // Extra small devices (phones in portrait)
      if (currentScreenSize.isSmall) return '-16 -16' // Small devices (phones in landscape, small tablets)
      if (currentScreenSize.isMedium) return '-20 -20' // Medium devices (tablets)
      return '-24 -24' // Large devices (desktops)
    }
    config({
      floatingActionButtonConfig: {
        maxSpeedDialActionCount: 10,
        icon: 'add',
        label: '',
        shading: false,
        position: {
          my: 'right bottom',
          at: 'right bottom',
          of: window,
          offset: getOffset(),
        },
      },
    })
  }, [currentScreenSize])

  useEffect(() => {
    responsiveBoxRef.current?.instance()?.repaint()
  }, [dbNormalView])

  return (
    <>
      <div className={'content-block dashboard-container'} id="dashboard">
        <div>
          {dbCards.length > 0 ? (
            <ResponsiveBox ref={responsiveBoxRef}>
              <Row ratio={1} />
              {dbCards.length > 1 ? <Row ratio={1} /> : <></>}
              {dbCards.length > 2 ? <Row ratio={1} screen="xs sm" /> : <></>}
              {dbCards.length > 3 ? <Row ratio={1} screen="xs sm" /> : <></>}
              <Col ratio={1} />
              {dbCards.length > 2 ? <Col ratio={1} screen="md lg" /> : <></>}
              {dbCards.map((card: string, index: number) => {
                return (
                  <Fragment key={card}>
                    <ResponsiveItem>
                      <Location
                        screen="md lg"
                        row={
                          dbNormalView
                            ? dbCards.length <= 2
                              ? index
                              : dbCards.length === maxCardsVisible
                                ? index < 2
                                  ? 0
                                  : 1
                                : index < 1
                                  ? 0
                                  : 1
                            : dbCards.length <= 2
                              ? index
                              : dbCards.length === maxCardsVisible
                                ? index < 2
                                  ? 0
                                  : 1
                                : index <= 1
                                  ? 0
                                  : 1
                        }
                        col={
                          dbNormalView
                            ? dbCards.length <= 2
                              ? 0
                              : dbCards.length === maxCardsVisible
                                ? index === 0 || index === 2
                                  ? 0
                                  : 1
                                : index === 0 || index === 1
                                  ? 0
                                  : 1
                            : dbCards.length <= 2
                              ? 0
                              : index === 0 || index === 2
                                ? 0
                                : 1
                        }
                        colspan={
                          dbNormalView
                            ? dbCards.length === 3 && index === 0
                              ? 2
                              : 1
                            : dbCards.length === 3 && index === 2
                              ? 2
                              : 1
                        }
                      />
                      <Location screen="xs sm" row={index} col={0} />
                      <DashboardCard
                        card={card}
                        index={index}
                        cardsVisible={dbCards.length}
                        cardsView={dbNormalView}
                        maxCardsVisible={maxCardsVisible}
                        onRemoveMenuButtonClick={(e: ClickEvent) => {
                          removeCard(dbCards.filter((c) => c !== card))
                        }}
                        onMoveLeftMenuButtonClick={(e: ClickEvent) => {
                          switchCards(index, index - 1)
                        }}
                        onMoveRightMenuButtonClick={(e: ClickEvent) => {
                          switchCards(index, index + 1)
                        }}
                        onMoveUpMenuButtonClick={(e: ClickEvent) => {
                          if (dbCards.length === 3 && !dbNormalView) switchViewMode(true)
                          else switchCards(index, index === 2 || index === 3 ? index - 2 : index - 1)
                        }}
                        onMoveDownMenuButtonClick={(e: ClickEvent) => {
                          if (dbCards.length === 3 && dbNormalView) switchViewMode(false)
                          else switchCards(index, index === 0 && dbCards.length === 2 ? index + 1 : index + 2)
                        }}
                        getCurrentEmployee={getCurrentEmployee}
                      ></DashboardCard>
                    </ResponsiveItem>
                  </Fragment>
                )
              })}
            </ResponsiveBox>
          ) : (
            <></>
          )}
        </div>
        <SpeedDialAction
          icon="event"
          label="Prossimi impegni"
          index={1}
          onClick={(e: SpeedDialActionTypes.ClickEvent) => {
            if (dbCards.length === maxCardsVisible) {
              notify(`È possibile aggiungere un massimo di ${maxCardsVisible} card.`, 'warning', 3000)
              return
            }
            updateCards([...dbCards, Card.NextCommitmentsCard])
          }}
          visible={!dbCards.find((card) => card === Card.NextCommitmentsCard)}
        />
        <SpeedDialAction
          icon="checklist"
          label="Promemoria"
          index={2}
          visible={!dbCards.find((card) => card === Card.ReminderCard)}
          onClick={(e: SpeedDialActionTypes.ClickEvent) => {
            if (dbCards.length === maxCardsVisible) {
              notify(`È possibile aggiungere un massimo di ${maxCardsVisible} card.`, 'warning', 3000)
              return
            }
            updateCards([...dbCards, Card.ReminderCard])
          }}
        />
        <SpeedDialAction
          icon="accountbox"
          label="Dal tuo account"
          index={3}
          visible={!dbCards.find((card) => card === Card.FromYourAccountCard)}
          onClick={(e: SpeedDialActionTypes.ClickEvent) => {
            if (dbCards.length === maxCardsVisible) {
              notify(`È possibile aggiungere un massimo di ${maxCardsVisible} card.`, 'warning', 3000)
              return
            }
            updateCards([...dbCards, Card.FromYourAccountCard])
          }}
        />
        <SpeedDialAction
          icon="chart"
          label="Riepilogo attività ultima settimana"
          index={4}
          visible={!dbCards.find((card) => card === Card.ActivitiesSummaryCard)}
          onClick={(e: SpeedDialActionTypes.ClickEvent) => {
            if (dbCards.length === maxCardsVisible) {
              notify(`È possibile aggiungere un massimo di ${maxCardsVisible} card.`, 'warning', 3000)
              return
            }
            updateCards([...dbCards, Card.ActivitiesSummaryCard])
          }}
        />
        {isUserRoleAllowed(userInfo?.roles, [Roles.Administrator, Roles.GlobalAdministrator]) && (
          <SpeedDialAction
            icon="fas fa-clipboard-list"
            label="Ordini da evadere"
            index={5}
            visible={!dbCards.find((card) => card === Card.OrdersToProcessCard)}
            onClick={(e: SpeedDialActionTypes.ClickEvent) => {
              if (dbCards.length === maxCardsVisible) {
                notify(`È possibile aggiungere un massimo di ${maxCardsVisible} card.`, 'warning', 3000)
                return
              }
              updateCards([...dbCards, Card.OrdersToProcessCard])
            }}
          />
        )}
        {isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.TechDeveloper]) && (
          <SpeedDialAction
            icon="fas fa-tasks"
            label="Task aperti"
            index={6}
            visible={!dbCards.find((card) => card === Card.TasksToCompleteCard)}
            onClick={(e: SpeedDialActionTypes.ClickEvent) => {
              if (dbCards.length === maxCardsVisible) {
                notify(`È possibile aggiungere un massimo di ${maxCardsVisible} card.`, 'warning', 3000)
                return
              }
              updateCards([...dbCards, Card.TasksToCompleteCard])
            }}
          />
        )}
        {isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.TechDeveloper]) && (
          <SpeedDialAction
            icon="fas fa-ticket"
            label="Riepilogo ticket aperti/assegnati"
            index={7}
            visible={!dbCards.find((card) => card === Card.TicketsSummaryCard)}
            onClick={(e: SpeedDialActionTypes.ClickEvent) => {
              if (dbCards.length === maxCardsVisible) {
                notify(`È possibile aggiungere un massimo di ${maxCardsVisible} card.`, 'warning', 3000)
                return
              }
              updateCards([...dbCards, Card.TicketsSummaryCard])
            }}
          />
        )}
        {isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.TechDeveloper]) && (
          <SpeedDialAction
            icon="fas fa-file-text"
            label="Analisi aperte"
            index={8}
            visible={!dbCards.find((card) => card === Card.AnalysisToCompleteCard)}
            onClick={(e: SpeedDialActionTypes.ClickEvent) => {
              if (dbCards.length === maxCardsVisible) {
                notify(`È possibile aggiungere un massimo di ${maxCardsVisible} card.`, 'warning', 3000)
                return
              }
              updateCards([...dbCards, Card.AnalysisToCompleteCard])
            }}
          />
        )}
      </div>
    </>
  )
}

export default DashboardPage
